import { Component, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import { Fieldset } from '../../../../shared/components/ui/fieldset/fieldset';
import { PermissionStore } from '../../../../state/permissions.repository';
import { AsyncPipe } from '@angular/common';
import { AppService } from '../../../../core/services/app.service';
import {
  SelectField,
  SelectOption,
} from '../../../../shared/components/form-fields/select-field/select-field';
import { CreateRole } from '../../../../shared/components/ui/create-role/create-role';
import {
  CreateRoleRequest,
  GetRoleResponse,
  PrivilegesApi,
  privilegesApiSchema,
  Role,
  RoleWithPassword,
} from '../../../../shared/schemas/backend-api';
import { TextField } from '../../../../shared/components/form-fields/text-field/text-field';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SimpleLogin } from '../../../../core/components/simple-login/simple-login';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { PasswordConfirmInputs } from '../../../../shared/components/form-fields/password-confirm-inputs/password-confirm-inputs';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCheckCircle, faClose } from '@fortawesome/pro-duotone-svg-icons';
import { IconBtn } from '../../../../shared/components/ui/buttons/icon-btn/icon-btn';
import { faAdd } from '@fortawesome/pro-solid-svg-icons/faAdd';
import { faMinus, faStar } from '@fortawesome/pro-solid-svg-icons';
import { Tab, Tabs } from '../../../../shared/components/ui/tabs/tabs';
import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons';
import { HttpErrorResponse } from '@angular/common/http';
import { Button } from '../../../../shared/components/ui/buttons/gradient-btn/button';
import { Notice } from '../../../../shared/components/ui/notice/notice';
import { SettingsShell } from '../settings-shell/settings-shell';
import {
  TransferDefaultDialog,
  TransferDefaultDialogData,
  TransferDefaultDialogResult,
} from './transfer-default-dialog/transfer-default-dialog';
import {
  defaultPrivileges,
  forbiddenDefaultPrivileges,
} from '../../../../shared/constants/privileges-local';
import { PasswordRecoveryService } from '../../../../core/services/password-recovery.service';

@Component({
  selector: 'andor-settings-permissions',
  standalone: true,
  imports: [
    Fieldset,
    AsyncPipe,
    SelectField,
    CreateRole,
    TextField,
    ReactiveFormsModule,
    SimpleLogin,
    TranslatePipe,
    PasswordConfirmInputs,
    Notice,
    FaIconComponent,
    IconBtn,
    Tabs,
    FaDuotoneIconComponent,
    Button,
    SettingsShell,
  ],
  templateUrl: './settings-permissions.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './settings-permissions.scss',
})
export class SettingsPermissions {
  protected readonly faAdd = faAdd;
  protected readonly faMinus = faMinus;
  protected readonly faStar = faStar;
  protected readonly faStarRegular = faStarRegular;
  @ViewChild('passwordForm') passwordForm?: PasswordConfirmInputs;
  roles = new Map<string, GetRoleResponse>();
  roleOptions = signal<SelectOption[]>([]);
  protected readonly faClose = faClose;
  selectedRoleOption = signal<SelectOption | null>(null);
  mode = signal<'create' | 'edit'>('edit');
  error = signal<string | null>(null);
  success = signal<string | null>(null);
  errorNoRoles = signal<string | null>(null);
  selectedRole = signal<Role | null>(null);
  selectedRoleId?: string;
  newPassword = signal<string>('');
  newPasswordValid = signal(false);
  newPasswordTouched = signal(false);
  loading = signal(false);
  hasChanged = signal(false);
  roleNameControl = new FormControl('', [
    Validators.required,
    Validators.minLength(4),
    Validators.maxLength(20),
    // Validators.pattern(/[^a-zA-Z]/g),
  ]);
  tabs: Tab[] = [
    {
      id: 'team-settings',
      label: _('Team Settings'),
    },
    {
      id: 'team-permissions',
      label: _('Team Permissions'),
    },
  ];
  activeTab = signal<Tab>(this.tabs[0]);
  // Set after a password-recovery link was redeemed: pre-select that role and
  // nag for a new password (the old one still works until it is changed).
  readonly recovery = inject(PasswordRecoveryService);
  constructor(
    public permissionStore: PermissionStore,
    public appService: AppService,
    public translateService: TranslateService
  ) {
    // Reload roles whenever the session gains `editAppPermissions`, not just
    // once on construction. `GET /v1/roles` is gated on that privilege, so a
    // load attempted without it 403s ("Couldn't load any roles"); the error
    // must clear itself the moment the user regains the privilege — e.g. after
    // logging back in on this very screen, or after a default-role transfer
    // that momentarily dropped it — without needing the component to be torn
    // down and re-created. The elf selector emits its current value on
    // subscribe, so this also covers the initial load.
    this.permissionStore.editAppPermissions$
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((canEditPermissions) => {
        if (!canEditPermissions) return;
        this.loading.set(true);
        this.loadRoles().finally(() => this.loading.set(false));
      });
    this.roleNameControl.valueChanges.subscribe((value) => {
      const role = this.selectedRole();
      if (value && role) {
        role.name = value;
        this.selectedRole.set({ ...role });
      }
    });
  }

  private readonly _dialog = inject(MatDialog);

  /**
   * Initial load. After a recovery-link sign-in the recovered role is selected
   * instead of the default one, landing the user on its password fields.
   */
  private async loadRoles(): Promise<void> {
    const recovered = this.recovery.recoveredRole();
    await this.getRoles(!recovered);
    if (recovered) {
      const option = this.roleOptions().find((o) => o.id === recovered.id);
      if (option) this.selectRole(option);
      else this.selectDefaultRole();
    }
  }

  /**
   * Transfer the default to the selected role. The default can only move to
   * another, already-persisted role (the star is disabled for the current
   * default and in create mode), and the server requires a disposition for
   * the previously-default role — the dialog collects it: a new password
   * (members are signed out) or deletion.
   */
  async toggleDefault() {
    const role = this.selectedRole();
    const roleId = this.selectedRoleId;
    if (!role || !roleId || role.default || this.mode() === 'create') return;

    // A default role must never carry administrative privileges — it is what
    // every anonymous visitor gets. Block the promotion before the disposition
    // dialog, using the persisted privileges (what the server will evaluate).
    const persisted = this.roles.get(roleId) ?? role;
    const forbidden = forbiddenDefaultPrivileges(persisted.privileges);
    if (forbidden.length > 0) {
      const names = forbidden
        .map((id) => this.translateService.instant(this.privilegeLabel(id)))
        .join(', ');
      this.setError(
        this.translateService.instant(
          'You can\'t make "[role]" the default team: the default team must not have these permissions active: [permissions].',
          { role: role.name, permissions: names }
        )
      );
      return;
    }

    const oldDefault = [...this.roles.values()].find((r) => r.default);
    let result: TransferDefaultDialogResult;
    if (oldDefault) {
      const data: TransferDefaultDialogData = {
        newDefaultName: role.name,
        oldDefaultName: oldDefault.name,
      };
      const ref = this._dialog.open(TransferDefaultDialog, {
        panelClass: 'confirm-dialog',
        backdropClass: 'confirm-backdrop',
        data,
      });
      result = (await firstValueFrom(ref.afterClosed())) as TransferDefaultDialogResult;
      if (!result) return;
    }

    this.loading.set(true);
    try {
      await this.appService.transferDefaultRole(roleId, result);
      await this.getRoles(false);
      this.selectRole({ id: roleId, name: role.name });
      this.setSuccess(`The team ${role.name} is now the default team.`);
    } catch (e) {
      this.handleResponseError(e);
      await this.getRoles(false);
    }
    this.loading.set(false);
  }

  async getRoles(selectDefault: boolean = true) {
    this.errorNoRoles.set(null);
    // Rebuild from scratch so roles deleted elsewhere don't linger.
    this.roles.clear();
    try {
      const result = await this.appService.getRoles();
      const options: SelectOption[] = [];
      let defaultOption: SelectOption | undefined;
      for (const role in result) {
        this.roles.set(role, result[role]);
        const option = {
          name: result[role].name,
          tag: result[role].default ? 'default' : undefined,
          id: role,
        };
        options.push(option);
        if (result[role].default) {
          defaultOption = option;
        }
      }
      options.sort((a, b) => a.name.localeCompare(b.name));
      this.roleOptions.set(options);
      if (selectDefault) {
        if (defaultOption) {
          this.selectRole(defaultOption);
        }
      }
    } catch (e) {
      // The roles Map was already cleared above — clear the derived UI state
      // too, or the dropdown keeps offering stale options that selectRole()
      // silently ignores (its Map lookup misses).
      this.roleOptions.set([]);
      this.selectedRoleOption.set(null);
      this.selectedRole.set(null);
      this.errorNoRoles.set("Couldn't load any roles");
    }
  }

  selectRole(option: SelectOption) {
    this.activeTab.set(this.tabs[0]);
    if (!option.id) return;
    // Get the original, unaltered role
    const role = this.roles.get(option.id);
    console.log('selectRole() role: ', role);
    if (!role) return;
    const { id, ...selectedRole } = role;
    this.selectedRoleId = id;
    this.clearPassword();
    this.selectedRoleOption.set({ ...option });
    this.selectedRole.set({
      ...selectedRole,
      privileges: { ...selectedRole.privileges },
    });
    this.roleNameControl.patchValue(selectedRole.name);
  }

  resetCurrentRole() {
    const selectedRole = this.selectedRoleOption();
    if (selectedRole) {
      this.selectRole(selectedRole);
    }
  }

  selectDefaultRole() {
    this.activeTab.set(this.tabs[0]);
    const defaultRole = this.roleOptions().find((option) => {
      return option.tag === 'default';
    });
    if (!defaultRole) return;
    this.selectRole(defaultRole);
    this.selectedRoleOption.set({ ...this.roleOptions()[0] });
    this.mode.set('edit');
  }

  newRole() {
    this.clearPassword();
    this.selectedRole.set({
      name: '',
      default: false,
      privileges: privilegesApiSchema.parse({}),
    });
    this.mode.set('create');
    this.activeTab.set(this.tabs[0]);
    this.selectedRoleId = crypto.randomUUID();
    this.roleNameControl.patchValue('');
  }

  updatePrivileges(role: Role, update: PrivilegesApi) {
    this.hasChanged.set(true);
    this.selectedRole.set({
      ...role,
      privileges: {
        ...update,
      },
    });
  }

  async createRole() {
    this.loading.set(true);
    this.activeTab.set(this.tabs[0]);
    const role = this.selectedRole();
    const password = this.newPassword();
    if (!this.selectedRoleId || !password.length || !role) {
      this.setError('Invalid name or password.');
      return;
    }
    try {
      // Typed as CreateRoleRequest so the required password is enforced here,
      // where creating is decided — `saveRole` itself accepts either shape.
      const payload: CreateRoleRequest = {
        ...role,
        password,
      };
      const result = await this.appService.saveRole(this.selectedRoleId, payload);
      await this.getRoles(false);
      this.selectRole({ id: result.id, name: result.name });
      this.setSuccess(`The role ${role.name} has been created successfully.`);
      this.mode.set('edit');
    } catch (e) {
      this.handleResponseError(e);
    }
    this.loading.set(false);
  }

  async updateRole() {
    this.loading.set(true);
    const role = this.selectedRole();
    if (!this.selectedRoleId || !role) {
      this.setError('Something went wrong when updating.');
      await this.getRoles(true);
      this.loading.set(false);
      return;
    }
    try {
      const payload: RoleWithPassword = {
        ...role,
      };
      const password = this.newPassword();
      if (password.length && this.newPasswordValid()) {
        payload.password = password;
      }
      const result = await this.appService.saveRole(this.selectedRoleId, payload);
      // A saved password settles the recovery reminder.
      if (payload.password && this.recovery.recoveredRole()?.id === this.selectedRoleId) {
        this.recovery.clearRecoveredRole();
      }
      await this.getRoles(false);
      this.selectRole({ id: result.id, name: result.name });
      this.setSuccess(`The role ${role.name} has been updated successfully.`);
      this.mode.set('edit');
    } catch (e) {
      this.handleResponseError(e);
    }
    this.loading.set(false);
  }

  async deleteRole() {
    if (this.selectedRole()?.default) {
      this.setError(
        'You are not allowed to delete the default team. Please make another the default team and try again.'
      );
      return;
    }
    if (this.selectedRoleId) {
      try {
        const result = await this.appService.deleteRole(this.selectedRoleId);
        await this.getRoles();
        this.mode.set('edit');
      } catch (e) {
        this.setError('Error when deleting role');
      }
    }
  }

  clearPassword() {
    this.passwordForm?.reset();
    this.newPassword.set('');
  }

  handleResponseError(e: unknown) {
    let errorMsg = "Couldn't save the role.";
    if (e instanceof HttpErrorResponse) {
      errorMsg += ` Reason: ${e.error.message}`;
    }
    this.setError(errorMsg);
  }

  setSuccess(text: string) {
    this.success.set(text);
    setTimeout(() => {
      this.success.set(null);
    }, 3000);
  }

  setError(text: string) {
    this.error.set(text);
    setTimeout(() => {
      this.error.set(null);
    }, 5000);
  }

  /** Human-readable (translation-marked) label for a privilege id. */
  private privilegeLabel(id: keyof PrivilegesApi): string {
    return defaultPrivileges.find((p) => p.id === id)?.label ?? id;
  }

  protected readonly faCheckCircle = faCheckCircle;

  async save() {
    // const result = await this._appConfigService.syncConfig();
    // console.log('save() result', result);
    // this._snackBar.open(
    //   this._translateService.instant('App Settings saved successfully!'),
    //   undefined,
    //   {
    //     duration: 3000,
    //     horizontalPosition: 'center',
    //     verticalPosition: 'top',
    //   }
    // );
    // this.oldState = this.appConfigStore.value;
    // this.closed.emit();
  }

  cancel() {
    // this.appConfigStore.update(this.oldState);
    // this.closed.emit();
  }
}
